/**
 * SCORM Service Types
 * Based on SCORM 1.2 specification and backend API contracts
 */

// Content Catalog SCORM item structure
export interface ScormContent {
  id: number;
  guid: string;
  type: string;
  delivery_method: "scorm";
  title: string;
  description: string;
  entry_point: string;
  package_id: string;
  version: string;
  friendly_url: string;
  duration_sec: number;
  difficulty_type: string;
  organization: string;
  publisher_name: string;
  created_at: string;
  updated_at: string;
  active: number;
  is_partial: number;
  is_private: number;
  language_code: string;
  locale_code: string;
}

// Launch API Response
export interface ScormLaunchResponse {
  attemptId: string;
  launchUrl: string;
  runtimeState: {
    "cmi.core.lesson_location": string;
    "cmi.suspend_data": string;
    "cmi.core.lesson_status": string;
    "cmi.core.score.raw": number;
    "cmi.core.score.min": number;
    "cmi.core.score.max": number;
    "cmi.core.exit": string;
  };
}

// Resume API Response
export interface ScormResumeResponse {
  hasAttempt: boolean;
  attemptId?: string;
  lesson_status?:
    | "not attempted"
    | "incomplete"
    | "completed"
    | "passed"
    | "failed";
  lesson_location?: string;
  suspend_data?: string;
  score_raw?: number;
  score_min?: number;
  score_max?: number;
  completed_at?: string | null;
}

// Runtime Event for telemetry (matches backend SingleEventRequest)
export interface ScormEvent {
  event_type:
    | "initialize"
    | "terminate"
    | "data_change"
    | "commit"
    | "interaction"
    | "progress"
    | "launch_initiated"
    | "launch_successful"
    | "session_ended";
  timestamp: string;
  data?: {
    element?: string;
    value?: string;
    [key: string]: any; // Allow additional fields
  };
}

// Event Request/Response (matches backend dto.ScormEventRequest)
export interface ScormEventRequest {
  attempt_id: string; // Backend expects lowercase with underscore
  events: ScormEvent[]; // Backend expects lowercase
}

export interface ScormEventResponse {
  success: boolean;
  total_count: number;
  processed: number;
  failed: number;
  results: Array<{
    success: boolean;
    error_code: string;
    message?: string;
  }>;
}

// Parse Response (for validation only)
export interface ScormParseResponse {
  valid: boolean;
  title: string;
  description: string;
  version: string;
  numScos: number;
  organization: {
    identifier: string;
    title: string;
  };
  resources: Array<{
    identifier: string;
    scormType: string;
    href: string;
    title: string;
  }>;
  errors: Array<{
    code: string;
    message: string;
  }>;
  warnings: Array<string>;
}

// Upload Response
export interface ScormUploadResponse {
  packageId: string;
  title: string;
  numScos: number;
  status: "ready" | "processing" | "error";
}

// SCORM Error Codes (SCORM 1.2 specification)
export enum ScormErrorCodes {
  NO_ERROR = "0",
  GENERAL_EXCEPTION = "101",
  GENERAL_INITIALIZATION_FAILURE = "102",
  ALREADY_INITIALIZED = "103",
  CONTENT_INSTANCE_TERMINATED = "104",
  GENERAL_TERMINATION_FAILURE = "111",
  TERMINATION_BEFORE_INIT = "112",
  TERMINATION_AFTER_TERM = "113",
  RETRIEVE_DATA_BEFORE_INIT = "122",
  RETRIEVE_DATA_AFTER_TERM = "123",
  STORE_DATA_BEFORE_INIT = "132",
  STORE_DATA_AFTER_TERM = "133",
  COMMIT_BEFORE_INIT = "142",
  COMMIT_AFTER_TERM = "143",
  GENERAL_ARGUMENT_ERROR = "201",
  GENERAL_GET_FAILURE = "301",
  GENERAL_SET_FAILURE = "401",
  GENERAL_COMMIT_FAILURE = "402",
  UNDEFINED_DATA_MODEL = "403",
  UNIMPLEMENTED_ELEMENT = "404",
  VALUE_NOT_INITIALIZED = "405",
  READ_ONLY_ELEMENT = "406",
  WRITE_ONLY_ELEMENT = "407",
  TYPE_MISMATCH = "408",
}

// Session States
export enum ScormSessionState {
  NOT_INITIALIZED = "not-initialized",
  RUNNING = "running",
  FINISHED = "finished",
}

// Supported SCORM Data Model Elements
export type ScormDataElement =
  | "cmi.core.student_id"
  | "cmi.core.student_name"
  | "cmi.core.lesson_location"
  | "cmi.core.credit"
  | "cmi.core.lesson_status"
  | "cmi.core.entry"
  | "cmi.core.score.raw"
  | "cmi.core.score.max"
  | "cmi.core.score.min"
  | "cmi.core.total_time"
  | "cmi.core.lesson_mode"
  | "cmi.core.exit"
  | "cmi.core.session_time"
  | "cmi.suspend_data"
  | "cmi.launch_data"
  | "cmi.comments"
  | "cmi.comments_from_lms";
